// @flow
import type {Product} from "../models/Product";
import {getGalleryPrice} from "../models/Product";
import {getBasename, getRouterMode, QUERY_PARAM_NAME, ROUTER_MODES} from "./routerMode";
import {encodeRouteForQuery, writeRouteToSearch} from "./queryHistory";

/**
 * Client-side page meta management: document.title and meta description per
 * route, canonical + robots per route (query/browser mode only), plus
 * schema.org Product JSON-LD on the detail page.
 *
 * The module is embedded into dealer pages, so the host page's original head
 * elements are captured on first use and restored on reset — the embed must
 * never permanently alter the host page's head.
 */

const JSONLD_ELEMENT_ID = "hpm-jsonld";
const META_DESCRIPTION_MAX_LENGTH = 160;
const JSONLD_DESCRIPTION_MAX_LENGTH = 500;
const MANAGED_ATTRIBUTE = "data-hpm-managed";
// tracking and module parameters never belong into a canonical URL
const STRIPPED_PARAMS = ["gclid", "fbclid", "msclkid", "an", "vehicle", "model", "vehiclekey", "theme", "category", QUERY_PARAM_NAME];
const ROUTE_PREFIX = /^\/brand\/[^/]+/;

let originalTitle: ?string = null;
let originalDescription: ?{existed: boolean, content: string} = null;
let originalCanonical: ?{existed: boolean, href: string} = null;
let originalRobots: ?{existed: boolean, content: string} = null;
let currentCanonical: ?string = null;
// the hosted variant keeps dealers out of the index whose own site is the canonical shop
let forceNoindex: boolean = false;

const getDescriptionMeta = () => document.head.querySelector("meta[name=\"description\"]");
const getCanonicalLink = () => document.head.querySelector("link[rel=\"canonical\"]");
const getRobotsMeta = () => document.head.querySelector("meta[name=\"robots\"]");

export const stripHtml = (html: ?string): string => {
    if (!html) {
        return "";
    }
    const container = document.createElement("div");
    container.innerHTML = html;
    return (container.textContent || "").replace(/\s+/g, " ").trim();
};

export const truncateAtWord = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }
    const cut = text.slice(0, maxLength - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > maxLength / 3 ? cut.slice(0, lastSpace) : cut) + "…";
};

export const setPageMeta = ({title, description}: {title?: ?string, description?: ?string}) => {
    if (title) {
        if (originalTitle == null) {
            originalTitle = document.title;
        }
        document.title = originalTitle ? `${title} | ${originalTitle}` : title;
    }

    if (description) {
        let meta = getDescriptionMeta();
        if (originalDescription == null) {
            originalDescription = {
                existed: !!meta,
                content: meta ? (meta.getAttribute("content") || "") : "",
            };
        }
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "description");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", truncateAtWord(description, META_DESCRIPTION_MAX_LENGTH));
    }
};

export const resetPageMeta = () => {
    if (originalTitle != null) {
        document.title = originalTitle;
        originalTitle = null;
    }

    if (originalDescription != null) {
        const meta = getDescriptionMeta();
        if (meta) {
            if (originalDescription.existed) {
                meta.setAttribute("content", originalDescription.content);
            } else {
                meta.remove();
            }
        }
        originalDescription = null;
    }
};

/* ---------------------------------------------------------------- canonical */

const isStrippedParam = (name: string): boolean => {
    const key = name.toLowerCase();
    return key.startsWith("utm_") || STRIPPED_PARAMS.includes(key);
};

const trimSlashes = (path: string): string => path.replace(/\/+$/, "") || "/";

const hostCanonicalHref = (): ?string => {
    if (originalCanonical != null) {
        return originalCanonical.existed ? originalCanonical.href : null;
    }
    const link = getCanonicalLink();
    return link ? link.getAttribute("href") : null;
};

/**
 * The host page's own address minus tracking/module parameters. The host's
 * canonical is trusted only when it points at the current path — dealer CMS
 * frequently emit a wrong or site-wide canonical.
 */
export const buildCanonicalBase = (): {origin: string, pathname: string, search: string} => {
    const {origin, pathname, search} = window.location;
    let base = {origin, pathname, search};

    const hostHref = hostCanonicalHref();
    if (hostHref) {
        try {
            const url = new URL(hostHref, window.location.href);
            if (url.origin === origin && trimSlashes(url.pathname) === trimSlashes(pathname)) {
                base = {origin: url.origin, pathname: url.pathname, search: url.search};
            }
        } catch (e) {
            // unusable host canonical — fall back to the location
        }
    }

    const kept = base.search.replace(/^\?/, "").split("&")
        .filter(entry => entry && !isStrippedParam(entry.split("=")[0]));
    return {origin: base.origin, pathname: base.pathname, search: kept.length ? `?${kept.join("&")}` : ""};
};

// null in hash mode: fragment URLs are not indexable, the host head stays untouched
export const buildCanonicalUrl = (route: string): ?string => {
    const mode = getRouterMode();
    if (mode === ROUTER_MODES.HASH) {
        return null;
    }
    const base = buildCanonicalBase();
    if (mode === ROUTER_MODES.QUERY) {
        return base.origin + base.pathname + writeRouteToSearch(base.search, QUERY_PARAM_NAME, route);
    }
    const path = `${getBasename()}${route}`.replace(/\/{2,}/g, "/");
    return base.origin + encodeRouteForQuery(path) + base.search;
};

export const setCanonical = (url: string) => {
    let link = getCanonicalLink();
    if (originalCanonical == null) {
        originalCanonical = {existed: !!link, href: link ? (link.getAttribute("href") || "") : ""};
    }
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
    }
    link.setAttribute(MANAGED_ATTRIBUTE, "");
    link.setAttribute("href", url);
    currentCanonical = url;
};

export const resetCanonical = () => {
    if (originalCanonical != null) {
        const link = getCanonicalLink();
        if (link) {
            if (originalCanonical.existed) {
                link.setAttribute("href", originalCanonical.href);
                link.removeAttribute(MANAGED_ATTRIBUTE);
            } else {
                link.remove();
            }
        }
        originalCanonical = null;
    }
    currentCanonical = null;
};

export const getCanonicalUrl = (): ?string => currentCanonical;

// what sharing and JSON-LD should point at
export const getShareUrl = (): string => currentCanonical || window.location.href;

export const setRobots = (content: string) => {
    let meta = getRobotsMeta();
    if (originalRobots == null) {
        originalRobots = {existed: !!meta, content: meta ? (meta.getAttribute("content") || "") : ""};
    }
    if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        document.head.appendChild(meta);
    }
    meta.setAttribute(MANAGED_ATTRIBUTE, "");
    meta.setAttribute("content", content);
};

export const resetRobots = () => {
    if (originalRobots != null) {
        const meta = getRobotsMeta();
        if (meta) {
            if (originalRobots.existed) {
                meta.setAttribute("content", originalRobots.content);
                meta.removeAttribute(MANAGED_ATTRIBUTE);
            } else {
                meta.remove();
            }
        }
        originalRobots = null;
    }
};

export const resetRouteMeta = () => {
    resetCanonical();
    resetRobots();
};

export const setForceNoindex = (flag: boolean) => {
    forceNoindex = !!flag;
};

/* ------------------------------------------------------------- route policy */

export const ROUTE_TYPES = Object.freeze({HOME: "home", PRODUCT: "product", CATEGORY: "category", NOINDEX: "noindex", OTHER: "other"});

export const classifyRoute = (pathname: ?string): string => {
    const path = (pathname || "/").replace(ROUTE_PREFIX, "") || "/";
    if (path === "/" || path === "/shop" || path === "/shop/") {
        return ROUTE_TYPES.HOME;
    }
    if (path.startsWith("/product/")) {
        return ROUTE_TYPES.PRODUCT;
    }
    if (path.startsWith("/products")) {
        return ROUTE_TYPES.CATEGORY;
    }
    if (path.startsWith("/search") || path.startsWith("/checkout") || path.startsWith("/bookmarks")) {
        return ROUTE_TYPES.NOINDEX;
    }
    return ROUTE_TYPES.OTHER;
};

const shopRouteFor = (pathname: ?string): string => {
    const match = ROUTE_PREFIX.exec(pathname || "");
    return `${match ? match[0] : ""}/shop`;
};

/**
 * Index policy per route: product and category pages are indexable and get
 * their own canonical; search, checkout and bookmarks are addressable in
 * query/browser mode but must stay out of the index; home restores the host.
 */
export const applyRoutePolicy = (pathname: ?string) => {
    if (getRouterMode() === ROUTER_MODES.HASH) {
        return;
    }
    switch (classifyRoute(pathname)) {
        case ROUTE_TYPES.PRODUCT:
        case ROUTE_TYPES.CATEGORY: {
            const url = buildCanonicalUrl(pathname || "/");
            if (url) {
                setCanonical(url);
            }
            resetRobots();
            break;
        }
        case ROUTE_TYPES.NOINDEX: {
            setRobots("noindex,follow");
            const url = buildCanonicalUrl(shopRouteFor(pathname));
            if (url) {
                setCanonical(url);
            }
            break;
        }
        default:
            resetCanonical();
            resetRobots();
    }
    if (forceNoindex) {
        setRobots("noindex,follow");
    }
};

/* ------------------------------------------------------------------ JSON-LD */

export const setProductJsonLd = (product: Product, options: {brandName?: ?string} = {}) => {
    const description = truncateAtWord(stripHtml(product.description), JSONLD_DESCRIPTION_MAX_LENGTH);
    const image = product.galleryImage && product.galleryImage.attachmentUrl;
    const price = product.distributionChannels ? getGalleryPrice(product) : product.price;

    const jsonLd: Object = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
    };
    if (product.articleNumber) {
        jsonLd.sku = product.articleNumber;
        jsonLd.mpn = product.articleNumber;
    }
    if (description) {
        jsonLd.description = description;
    }
    if (image) {
        jsonLd.image = image;
    }
    if (options.brandName) {
        jsonLd.brand = {"@type": "Brand", name: options.brandName};
    }
    if (price && price.value != null && price.currency) {
        jsonLd.offers = {
            "@type": "Offer",
            price: String(price.value),
            priceCurrency: price.currency,
            url: getShareUrl(),
        };
        if (product.isAvailable) {
            jsonLd.offers.availability = "https://schema.org/InStock";
        }
    }

    let script = document.getElementById(JSONLD_ELEMENT_ID);
    if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = JSONLD_ELEMENT_ID;
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
};

export const removeProductJsonLd = () => {
    const script = document.getElementById(JSONLD_ELEMENT_ID);
    if (script) {
        script.remove();
    }
};
